import { supabase } from './supabase'; // Assuming supabase client is exported from here
import { logger } from './logger';
import { logRocketSessionURL } from '../main'; // Import LogRocket Session URL
import { getPlatformInfo } from '../utils/platformUtils';
import { getCurrentTimezoneOffset } from '../utils/getCurrentTimezoneOffset';
import { getBackendApiUrl } from '../atoms/shared/regionAtom';
import i18n from './i18n';

/**
 * 处理401未授权错误的自动登出
 * 使用统一的登出机制，避免代码重复
 */
const handleUnauthorizedError = async () => {
  console.warn('[ApiClient] 检测到401未授权错误，触发自动登出');

  // 动态导入authUtils避免循环依赖，使用统一的登出逻辑
  try {
    const { performSignOut } = await import('../utils/authUtils');
    await performSignOut();
    console.log('[ApiClient] 使用统一登出机制成功');
  } catch (error) {
    console.error('[ApiClient] 统一登出机制失败，执行fallback:', error);
    // Fallback: 直接刷新页面
    window.location.reload();
  }
};

// 动态选择后端API URL：
// 基于IP地理位置检测选择最优后端API，支持CDN全球部署策略
const getApiBaseUrl = (): string => {
  // 直接根据地理位置选择后端API，无视环境变量
  // 因为CDN策略是一处部署全球使用，环境变量不可依赖
  const regionBasedUrl = getBackendApiUrl();
  if (regionBasedUrl) {
    return regionBasedUrl;
  }

  // 最后的fallback到全球版本
  return 'https://tempo-0y6p.onrender.com/';
};

const API_BASE_URL = getApiBaseUrl();

if (!API_BASE_URL) {
  logger.error('无法确定后端API URL。');
}

/**
 * 通用的 API 请求函数
 * @param path API 的相对路径 (e.g., '/tasks/list')
 * @param options Fetch API 的选项 (method, body, headers 等)
 * @returns 解析后的 JSON 响应数据
 * @throws 如果网络请求失败或 API 返回非 2xx 状态码，则抛出错误
 */
export const fetchApi = async <T = any>(
  path: string,
  options: Omit<RequestInit, 'body'> & { body?: any } = {},
): Promise<T> => {
  if (!API_BASE_URL) {
    throw new Error('API 基地址未配置 (VITE_BACKEND_API_URL)');
  }

  // 获取平台信息用于调试
  const platformInfo = getPlatformInfo();

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    const errorMessage = `获取 Supabase session 失败 (${platformInfo.os}): ${sessionError.message}`;
    console.error(errorMessage, {
      platform: platformInfo.os,
      isAndroid: platformInfo.isAndroid,
      isNative: platformInfo.isNative,
      userAgent: platformInfo.userAgent,
      error: sessionError
    });
    logger.error(errorMessage, sessionError, {
      platform: platformInfo.os,
      isAndroid: platformInfo.isAndroid,
      isNative: platformInfo.isNative,
      apiRequest: true,
      path: path
    });
    throw new Error('无法获取用户认证信息');
  }

  const token = sessionData.session?.access_token;

  // 准备请求头
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // 添加 LogRocket Session URL 请求头
  if (logRocketSessionURL) {
    headers.set('X-LogRocket-URL', logRocketSessionURL);
  }
  // 添加时区偏移量请求头
  headers.set('x-timezone', getCurrentTimezoneOffset().toString());

  // 如果请求体是对象但不是 FormData，自动设置为 JSON
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    options.body = JSON.stringify(options.body);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  const url = `${API_BASE_URL.replace(/\/$/, '')}${path.startsWith('/') ? path : '/' + path}`;
  const method = options.method || 'GET';

  // 增强的调试日志，包含平台信息
  logger.debug(`发起 API 请求: ${method} ${url} (${platformInfo.os})`, {
    important: false,
    path,
    method,
    hasBody: !!options.body,
    platform: platformInfo.os,
    isAndroid: platformInfo.isAndroid,
    isNative: platformInfo.isNative,
    hasToken: !!token,
    tokenLength: token ? token.length : 0
  });

  // Android 平台额外调试信息
  if (platformInfo.isAndroid) {
    console.log(`[Android API Debug] ${method} ${url}`, {
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
      userAgent: platformInfo.userAgent,
      headers: Object.fromEntries(headers.entries()),
      networkState: navigator.onLine ? 'online' : 'offline',
      connectionType: (navigator as any).connection?.effectiveType || 'unknown'
    });
  }

  try {
    const startTime = Date.now();
    const response = await fetch(url, requestOptions);
    const duration = Date.now() - startTime;

    if (!response.ok) {
      // 尝试解析错误响应体
      let errorBody;
      try {
        errorBody = await response.json();
      } catch (e) {
        // 如果响应体不是 JSON 或解析失败
        errorBody = { message: response.statusText };
      }

      // 记录错误 - Pass the specific error message or a new Error object
      const logErrorMessage = `API ${method} ${path} - ${response.status}`;
      const errorToLog = new Error(errorBody?.error || errorBody?.message || 'API Error'); // Create an Error object

      logger.error(
        logErrorMessage,
        errorToLog, // Pass the new Error object
        {
          responseBody: errorBody, // Keep original body in custom data if needed
          duration,
          status: response.status,
          apiRequest: true,
          path: path,
          method: method,
          platform: platformInfo.os,
          isAndroid: platformInfo.isAndroid,
          isNative: platformInfo.isNative
        }
      );

      // Android 平台额外错误日志
      if (platformInfo.isAndroid) {
        console.error(`[Android API Error] ${response.status} ${method} ${path}`, {
          errorBody,
          duration,
          userAgent: platformInfo.userAgent,
          url: url
        });
      }

      // 处理401未授权错误 - 自动登出
      if (response.status === 401) {
        console.warn('[ApiClient] 检测到401未授权，触发自动登出');
        // 异步执行登出，不阻塞当前错误处理
        handleUnauthorizedError().catch(logoutError => {
          console.error('[ApiClient] 自动登出过程中发生错误:', logoutError);
        });

        // 抛出特定的认证失败错误
        throw new Error('认证已失效，正在自动登出...');
      }

      throw new Error( // Keep the original throw logic
        `API 请求失败 (${response.status}): ${errorBody?.error || errorBody?.message || '未知错误'}`,
      );
    }

    // 处理没有内容的响应 (例如 204 No Content)
    if (response.status === 204) {
      // 记录成功的请求
      logger.info(`API ${method} ${path} - ${response.status}`, {
        duration,
        noContent: true,
        status: response.status,
        apiRequest: true,
        path: path,
        method: method
      });
      return undefined as T;
    }

    // 解析 JSON 响应
    const data: T = await response.json();

    // 记录成功的请求，但跳过 200 状态码的日志
    if (response.status !== 200) {
      logger.info(`API ${method} ${path} - ${response.status}`, {
        duration,
        dataSize: JSON.stringify(data).length,
        // 不记录完整响应数据，避免敏感信息泄露和日志过大
        hasData: !!data,
        status: response.status,
        apiRequest: true,
        path: path,
        method: method
      });
    }

    return data;
  } catch (error) {
    // 记录请求失败 - Ensure 'error' is an Error instance
    const networkErrorMessage = `网络请求或处理失败 (${method} ${url})`;
    const capturedError = error instanceof Error ? error : new Error(String(error)); // Ensure it's an Error

    logger.error(
      networkErrorMessage,
      capturedError, // Pass the ensured Error object
      {
        path,
        method,
        networkError: true,
        platform: platformInfo.os,
        isAndroid: platformInfo.isAndroid,
        isNative: platformInfo.isNative,
        // Add original error details if it wasn't an Error instance
        ...(!(error instanceof Error) && { originalError: String(error) })
      }
    );

    // Android 平台网络错误特殊处理
    if (platformInfo.isAndroid) {
      console.error(`[Android Network Error] ${method} ${path}`, {
        error: capturedError.message,
        stack: capturedError.stack,
        userAgent: platformInfo.userAgent,
        url: url,
        apiBaseUrl: API_BASE_URL
      });
    }

    // 重新抛出错误，可以是原始错误或包装后的错误
    if (error instanceof Error) {
      // 避免在错误信息中重复URL，因为已包含在 data 中
      throw new Error(`${i18n.t('common:errors.networkRequestError', '网络请求或处理过程中发生错误')} (URL: ${path}): ${error.message}`);
    }
    throw new Error(i18n.t('common:errors.unknownNetworkError', '网络请求或处理过程中发生未知错误'));
  }
};

